import * as React from 'react';
import BOE from './tracking.boe';
import Courier from './tracking.courier';
import Container from './tracking.container';
import GenericHoc from 'src/components/hoc/GenericHoc';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { StylesProps } from 'src/Library/Types';
import { TrackingTabs } from './tracking.constants';
import { trackingStyles } from './tracking.styles';
import GetExecutionData from 'src/dto/response/getExecution';
import { stops } from 'src/Library/Helper';
import { ExecutionDetails } from '../SEDetails/shipment-details.types';
import SEAddTrackingModal from './add-tracking-modal';
import SEAddAirTrackingModal from './add-air-tracking-modal';
import SEAddCourierTrackingModal from './add-courier-tracking-modal';
import { getOrgPrettyName } from 'src/api/utils';
import SELinkBlNumber from "./add-linked-bl-modal";
import { useEffect, useState } from 'react';
import Cargo from './tracking.air-cargo';
import { ModesOfService } from 'src/Library/Constants';
import { bindActionCreators } from 'redux';
import { updateCardDataAtIndex } from 'src/actions/shipmentExecutionActions';
import { SEContainerCardData, SEOrgType } from '../../SE.interfaces';
import { addLinkedBlNumberToContainer, sendShipmentTenderEmail } from 'src/api/shipmentExecution';
import { Button, Modal, Spin, message } from 'antd';
import CourierV2 from './tracking.courier.v2';

interface TrackingProps extends StylesProps<typeof trackingStyles> {
  selectedShipment: GetExecutionData;
  shipmentDetails: ExecutionDetails;
  type: SEOrgType;
  updateCardData: () => void;
  refreshShipmentDetails: () => void;
  shipmentTenderLoader: boolean;
  showCourierTrackingV2: boolean;
}

const ShipmentTracking = (props: TrackingProps) => {
  const { classes, selectedShipment, shipmentDetails, updateCardData, refreshShipmentDetails, showCourierTrackingV2 } = props;

  const [isAnyShipmentInTransitState, setisAnyShipmentInTransitState] = React.useState<boolean>(false);
  const [active, setActive] = React.useState<keyof typeof TrackingTabs>('container');
  const [isAddTrackingModalVisible, setIsAddTrackingModalVisible] = React.useState<boolean>(false);
  const [isAddCourierTrackingModalVisible, setisAddCourierTrackingModalVisible] = React.useState<boolean>(false);

  const [shipmentTenderLoader, showShipmentTenderLoader] = React.useState<boolean>(false);
  const [hiddenTabs, setHiddenTabs] = useState<(keyof typeof TrackingTabs)[]>();
  const [isAddLinkedBlNumberModalVisible, setIsAddLinkedBlNumberModalVisible] = React.useState<boolean>(false);
  const [bulkSelectedContainers, setBulkSelectedContainers] = React.useState<string[]>();
  const [isLinkedBlModalButtonEnabled, setIsLinkedBlModalButtonEnabled] = React.useState<boolean>(false);

  useEffect(() => {
    if (shipmentDetails?.id) {
      refreshShipmentDetails();
    }
  }, [])

  useEffect(() => {
    if (!shipmentDetails) {
      return;
    }

    if (
      shipmentDetails.mode === ModesOfService.AIRCARGO ||
      shipmentDetails.mode === ModesOfService.AIR
    ) {
      setHiddenTabs(['container']);
      setActive('cargo');
      return;
    };
    // Road-type modes don't have sea containers: hide the Container sub-tab, default to Courier. See ISS-296157.
    // Trailer is intentionally excluded - it keeps the Container sub-tab (unchanged from prior behaviour).
    if (
      shipmentDetails.mode === ModesOfService.DOMESTIC ||
      shipmentDetails.mode === ModesOfService.RPTL
    ) {
      setHiddenTabs(['cargo', 'container']);
      setActive('courier');
      return;
    };
    setHiddenTabs(['cargo']);
  }, [shipmentDetails?.mode]);
  
  const callShipmentTenderEmailApi = async () => {
    showShipmentTenderLoader(true);
    const params = {
      "executionReferenceNumbers": [shipmentDetails.referenceNumber]
    }
    const response = await sendShipmentTenderEmail(params);
    showShipmentTenderLoader(false);
    if (response?.isSuccess) {
      message.success(
        `Shipment tender email for ${shipmentDetails.referenceNumber} has been sent.`
      );
    } else {
      message.error(response?.errorMessage || 'Something went wrong, please try again later.');
    }
  }

//   const loadCourierItems = async () => {
//     //setLoading(true);
//     const result = await fetchCouriersOfShipment(selectedShipment.id);
//     if (result.isSuccess) {
//         result?.data.forEach(item => {
//             item.key = item.id
//         })
//         //console.log(result.data)
//         return result.data
//     }
//     return []
//     //setLoading(false);
// };

  const renderAddCourierModal = () => {
    return (
      isAddCourierTrackingModalVisible && (
        <SEAddCourierTrackingModal
          isVisible={isAddCourierTrackingModalVisible}
          data={shipmentDetails}
          onClose={() => {
            setisAddCourierTrackingModalVisible(false);
            // Container sub-tab is hidden for road-type modes; return to Courier instead. See ISS-296157.
            setActive(hiddenTabs?.includes('container') ? 'courier' : 'container')
          }}
        />
      )
    );
  }

  async function linkBlNumberToContainers(linkedBlNumber) {
    try {
      setIsLinkedBlModalButtonEnabled(true);
      const response = await addLinkedBlNumberToContainer({
        linkedBlNumber,
        containerIds: bulkSelectedContainers
      });
      if (response?.isSuccess) {
        message.success('Linked BL Number added successfully.');
        setIsAddLinkedBlNumberModalVisible(false);
      } else {
        message.error(response?.errorMessage || 'Something went wrong, please try again later.');
      }
      setIsLinkedBlModalButtonEnabled(false);
    } catch (err) {
      setIsLinkedBlModalButtonEnabled(false);
      setIsAddLinkedBlNumberModalVisible(false);
      message.error('Something went wrong, please try again later.');
    }
  }

  const renderLinkBlModal = () => {
    if (!shipmentDetails) return null;
    return (
      <SELinkBlNumber
        isVisible={isAddLinkedBlNumberModalVisible}
        linkBlNumberToContainers={linkBlNumberToContainers}
        updateCardData={updateCardData}
        onClose={() => {
          setIsAddLinkedBlNumberModalVisible(false);
        }}
        referenceNumber={shipmentDetails?.referenceNumber}
      >
      </SELinkBlNumber>
    );
  }

  const renderAddTrackingModal = () => {
    if (!shipmentDetails) return null;
    if (
      shipmentDetails.mode === ModesOfService.AIRCARGO ||
      shipmentDetails.mode === ModesOfService.AIR
    ) {
      return (
        isAddTrackingModalVisible && (
          <SEAddAirTrackingModal
            isVisible={isAddTrackingModalVisible}
            data={shipmentDetails}
            shipmentId={selectedShipment.id}
            onClose={() => {
              updateCardData();
              setIsAddTrackingModalVisible(false);
            }}
          />
        )
      );
    }
    return (
      isAddTrackingModalVisible && (
        <SEAddTrackingModal
          isVisible={isAddTrackingModalVisible}
          data={shipmentDetails}
          shipmentId={selectedShipment.id}
          fromContainer={true}
          onClose={() => {
            updateCardData();
            setIsAddTrackingModalVisible(false);
          }}
        />
      )
    );
  };

    const handleSendShipmentTender = async (event) => {
      stops(event);
      Modal.confirm({
        title: `Are you sure?`,
        content: 'No shipments are In Transit. Send Shipment Tender mail anyway?',
        okText: 'Send',
        onOk: () => {
          callShipmentTenderEmailApi()
        },
        onCancel() {},
      });
    }

  const renderLinkBlNumberButton = () => {
    return (
      <div onClick={(e) => {
        if (bulkSelectedContainers?.length) {
          setIsAddLinkedBlNumberModalVisible(true)
        } else {
          message.error("Select container(s) to Link a BL.")
        }
      }}
      className={classes.addTrackingButton}
      style={{
        color: shipmentTenderLoader ? 'lightgrey' : '#1890FF',
        pointerEvents: shipmentTenderLoader ? 'none' : 'auto',
        cursor: shipmentTenderLoader ? 'not-allowed' : 'pointer'
      }
      }>
        Link BL Number
      </div>
    )
  }

  const renderSendTenderButton = () => {
    if (!shipmentDetails?.viewSendTenderButton) {
      return;
    }
    return (
      <div
        onClick={(e) => {
          if (!isAnyShipmentInTransitState) {
            handleSendShipmentTender(e);
          } else {
            callShipmentTenderEmailApi();
          }
        }}
        className={classes.addTrackingButton}
        style={{
          color: shipmentTenderLoader ? 'lightgrey' : '#1890FF',
          pointerEvents: shipmentTenderLoader ? 'none' : 'auto',
          cursor: shipmentTenderLoader ? 'not-allowed' : 'pointer'
        }}
      >
        Send Tender
        {shipmentTenderLoader && <Spin size="small" style={{ marginLeft: 8 }} />}
      </div>
    )
  }

  const renderAddTrackingButton = () => {
    const currentOrganisation = getOrgPrettyName();

    if ( !!shipmentDetails &&
      (currentOrganisation !== shipmentDetails.shipperId) &&
      !shipmentDetails.allowExternalStakeholderToAddTracking
    ) {
      return;
    }

    const disabled = selectedShipment.milestone === 'ON_HOLD' || selectedShipment.milestone === 'DELETED';
    if (active !== "courier") {
      return (
        <div
          onClick={(e) => {
            stops(e);
            setIsAddTrackingModalVisible(true);
          }}
          className={classes.addTrackingButton}
          style={{
            color: disabled ? 'lightgrey' : '#1890FF',
            pointerEvents: disabled ? 'none' : 'auto',
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
        >
          + Add Tracking
        </div>
      )
    }
    else {
      return (
        <div
          onClick={(e) => {
            stops(e);
            setisAddCourierTrackingModalVisible(true);
          }}
          className={classes.addTrackingButton}
          style={{
            color: disabled ? 'lightgrey' : '#1890FF',
            pointerEvents: disabled ? 'none' : 'auto',
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
        >
          + Add Tracking
        </div>
      )
    }
  }

  const isNotHidden = (key: keyof typeof TrackingTabs) => {
    return !hiddenTabs?.includes(key);
  }

  const renderHeader = () => {
    return <div className={classes.header}>
      <div className={classes.flexRow} style={{ width: '75%' }}>
        {Object.keys(TrackingTabs)
          .filter(isNotHidden)
          .map((key: keyof typeof TrackingTabs) => (
            <div
              key={key}
              style={{
                borderBottom: active === key ? '2px solid #006EC3' : 'none',
                fontWeight: active === key ? 'bold' : 'inherit',
              }}
              onClick={() => setActive(key)}
              className={classes.tab}>
              {TrackingTabs[key]}
            </div>
          ))}
      </div>
      {renderSendTenderButton()}
      {(active === 'container' || active === 'cargo' || active === 'courier') && renderAddTrackingButton()}
      {renderLinkBlNumberButton()}
    </div>
  };

  const renderContainer = () => {
    if (active !== 'container') {
      return null;
    }
    return <Container
      setisAnyShipmentInTransitState={setisAnyShipmentInTransitState}
      selectedShipment={selectedShipment}
      bulkSelectedContainers={bulkSelectedContainers}
      setBulkSelectedContainers={setBulkSelectedContainers}
      
    />;
  };

  const renderAirCargo = () => {
    if (active !== 'cargo') {
      return null;
    }

    return <Cargo
      selectedShipment={selectedShipment}
    />;
  }

  const renderCourier = () => {
    if (active !== 'courier') {
      return null;
    }
    if (showCourierTrackingV2) {
      return <CourierV2
        selectedShipment={selectedShipment}
        //loadShipments={loadCourierItems}
      />
    }
    return <Courier
      selectedShipment={selectedShipment}
      awbNumber={shipmentDetails?.awbNumber}
    />;
  };

  const renderBOE = () => {
    if (active !== 'boe') {
      return null;
    }
    return <BOE selectedShipment={selectedShipment} />;
  };

  return <div className={classes.main}>
    {renderHeader()}
    {renderContainer()}
    {renderAirCargo()}
    {renderCourier()}
    {renderBOE()}
    {renderAddTrackingModal()}
    {renderAddCourierModal()}
    {renderLinkBlModal()}
  </div>;
}

const mapDispatchToProps = (dispatch, ownProps) => (bindActionCreators({
  updateCardDataAtIndex,
}, dispatch));

const options: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: trackingStyles,
  },
};

const Tracking = GenericHoc(options)(ShipmentTracking);
export default Tracking;